import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./style.module.scss";
import JourneyHeader from "components/General/JourneyHeader";
import { useHistory } from "react-router-dom";
import { notification, Spin, Tooltip } from "antd";
import { apiRequest } from "util/services";
import { UNLOGGED, APP_MESSAGES } from "util/constants";
import JourneyStepItem from "components/Journey/JourneyStepItem";
import { useSelector, useDispatch } from "react-redux";
import * as actions from "redux/Dashboard/actions.js";
import DashboardHeader from "components/Dashboard/Header";
import DashboardPageChrome from "components/Dashboard/DashboardPageChrome";
import NestedTabs from "components/NestedTabs";
import { useTranslation } from "react-i18next";
import { formatAppDateTime } from "util/formatAppDate";
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";

const AUTO_REFRESH_MS = 60_000;
const Journey = ({
  authenticated,
  user,
  currentDashboard,
  shared,
  currentDashboardHash,
  match,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [statusStep, setStatusStep] = useState(null);
  const history = useHistory();
  const DashboardReducer = useSelector((state) => state.DashboardReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const autoRefreshTimerRef = useRef(null);

  useEffect(() => {
    if (authenticated === UNLOGGED) {
      history.push("/");
      return;
    }
    const currentDashboard = match.params.id || DashboardReducer.dashboard?.ID;
    if (!currentDashboard) {
      notification.error({
        message: t("notifications.42"),
      });
      history.push("/home");
      return;
    }
    initializeBoardProgress();

    autoRefreshTimerRef.current = setInterval(() => {
      initializeBoardProgress(true);
    }, AUTO_REFRESH_MS);

    return () => clearInterval(autoRefreshTimerRef.current);
  }, []);

  const initializeBoardProgress = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    const currentDashboard = match.params.id || DashboardReducer.dashboard?.ID;
    const result = await apiRequest("dashboards/v2/get", "POST", {
      dashboard_id: parseInt(currentDashboard, 10),
    });
    if (result?.status.ID === -1 || result?.status.ID === 5) {
      notification.info({
        message: t("notifications.44"),
        icon: (
          <InfoCircleOutlined
            style={{ color: theme === "dark" ? "white" : "black" }}
          />
        ),
      });
      history.push(`/home`);
      // history.push("/console/myDashboards");
      return;
    }
    dispatch({
      type: actions.UPDATE_Dashboard,
      payload: {
        ...result,
        loading: true,
      },
    });
    if (result) {
      // Dashboards stay "Collecting data" until the pipeline marks them Ready.
      const ready = result.dashboard?.Status === "Ready";
      setStatusStep({
        id: ready ? 5 : 2,
        title: ready
          ? t("journey_page.10")
          : t("journey_page.collectingData", { defaultValue: "Collecting data" }),
        info: ready
          ? t("journey_page.11")
          : t("journey_page.collectingDataInfo", {
              defaultValue:
                "We are collecting and analyzing your data. Results will appear here as soon as they are ready.",
            }),
        startedAt: formatDate(result.dashboard?.CreationDate),
        completedAt: ready
          ? formatDate(result.dashboard?.FinishDate || result.dashboard?.lastUpdated)
          : "",
        monolithSteps: result.monolith_steps || null,
      });
    } else {
      notification.error({
        message: t("notifications.94"),
      });
    }
    setLoading(false);
    setRefreshing(false);
    setLastRefreshed(new Date());
  };

  const formatDate = (date) => {
    if (!date) return "";
    return formatAppDateTime(date, i18n.language);
  };

  return (
    <div className={styles.mainWindow}>
      <DashboardPageChrome>
        <DashboardHeader
          shared={shared}
          currentDashboardHash={currentDashboardHash}
          user={user}
          topicsType="journey"
        />
        <NestedTabs />
      </DashboardPageChrome>
      <div className={styles.refreshBar}>
        {lastRefreshed && (
          <span className={styles.lastRefreshed}>
            {t("journey_page.lastRefreshed")}{" "}
            {lastRefreshed.toLocaleTimeString()}
          </span>
        )}
        <Tooltip title={t("journey_page.autoRefreshHint")}>
          <button
            className={styles.refreshBtn}
            onClick={() => initializeBoardProgress(true)}
            disabled={refreshing || loading}
          >
            <ReloadOutlined spin={refreshing} />
            {t("journey_page.refresh")}
          </button>
        </Tooltip>
      </div>
      <Spin spinning={loading}>
        <div className={styles.journeyContent}>
          <div className={styles.left} id="journey_steps">
            {statusStep && (
              <JourneyStepItem
                key={statusStep.id}
                step={statusStep}
                active={statusStep.id === 2}
                id={statusStep.id}
                monolithSteps={statusStep.monolithSteps}
              />
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default Journey;
